import { useTranslation } from "next-i18next";

import { cn } from "@/common/utils/css.utils";
import { PRO_BUYER_RESERVATIONS_ENABLED } from "@/config";
import { isOnHoldUntilActive } from "@/features/pro-buyer-reservations/utils/reservation-format";
import { useProductCard } from "@/features/search/components/product-card/product-card.context";

/**
 * Prices, straight from the API response — no recomputation. Only the display
 * rules live here: a sold product shows what it went for instead of a price, and
 * an `originalPrice` above what the buyer pays now renders struck through next
 * to the current price.
 */
export const ProductCardPrice = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { product, formatAmount, formatListingAmount, proBuyerPrice } =
    useProductCard();
  const {
    originalPrice,
    buyNowPrice,
    bidFrom,
    sold,
    soldAtPrice,
    listingPrices,
  } = product;
  /* WHO-5947 F1: no "Bid from" while the piece is reserved (offers are locked). */
  const reserved =
    PRO_BUYER_RESERVATIONS_ENABLED &&
    isOnHoldUntilActive(product.onHoldUntil ?? product.on_hold_until);

  /*
   * A sold product is no longer on offer: what it went for replaces the price, and nothing else on
   * this line applies. `soldAtPrice` can be null for a sold product (no order amount), and then the
   * asking price is all there is to show.
   */
  if (sold && soldAtPrice != null) {
    return (
      <div className={cn("flex flex-col space-y-0.5", className)}>
        <span className="product-card-price">
          {t("common:product-card-sold-for", {
            price: formatAmount(soldAtPrice),
          })}
        </span>
        <ListingPrice
          amount={formatListingAmount(listingPrices?.soldAtPrice)}
        />
      </div>
    );
  }

  /*
   * The Pro Buyer price comes in the listing's own currency (its query asks for no display
   * currency), so its block stays in that currency, the regular price included (WHO-5142).
   */
  const formatInListingCurrency = (amount: number | null | undefined) =>
    listingPrices ? formatListingAmount(amount) : formatAmount(amount);

  /*
   * WHO-5743: the gateway's Pro Buyer price is the price; the regular price is a helper line in the
   * style of "Bid from". No percentage and no strikethrough on the card.
   */
  if (proBuyerPrice && !sold) {
    return (
      <div
        className={cn("flex flex-col space-y-0.5", className)}
        data-testid="pro-buyer-price-card"
      >
        <span className="product-card-price">
          {formatInListingCurrency(proBuyerPrice.price.amount)}
        </span>
        {buyNowPrice != null && (
          <span className="font-body text-xs text-grey-500 md:text-sm">
            {t("common:pro-buyer-price-regular", {
              defaultValue: "Regular price {{price}}",
              price: formatInListingCurrency(
                listingPrices?.buyNowPrice ?? buyNowPrice
              ),
            })}
          </span>
        )}
      </div>
    );
  }

  const currentPrice = buyNowPrice ?? originalPrice;
  const showOriginalPrice =
    originalPrice != null && buyNowPrice != null && originalPrice > buyNowPrice;

  return (
    <div className={cn("flex flex-col space-y-0.5", className)}>
      <div className="product-card-price-row">
        {showOriginalPrice && (
          <span className="font-body text-sm text-grey-500 line-through">
            {formatAmount(originalPrice)}
          </span>
        )}
        <span className="product-card-price">{formatAmount(currentPrice)}</span>
      </div>
      <ListingPrice
        amount={formatListingAmount(
          listingPrices?.buyNowPrice ?? listingPrices?.originalPrice
        )}
      />
      {/*
        A minimum bid of zero means the auction has none. `formatAmount` renders 0 as an empty
        string, so testing for null alone left the line reading "Bid from" with nothing after it —
        on the live search results and on every gateway-backed list.
      */}
      {bidFrom != null && bidFrom > 0 && !reserved && (
        <span className="font-body text-xs text-grey-500 md:text-sm">
          {t("common:product-card-bid-from", { price: formatAmount(bidFrom) })}
        </span>
      )}
    </div>
  );
};

/**
 * The euro a converted card price came from, small under it (WHO-5991 decision 7, as in the
 * apps): the converted amount is a guide, this is what is charged. Nothing when the card wasn't
 * converted.
 */
const ListingPrice = ({ amount }: { amount: string | null }) =>
  amount ? (
    <span
      className="font-body text-xs text-grey-500 md:text-sm"
      data-testid="product-card-listing-price"
    >
      {amount}
    </span>
  ) : null;
